/**
 * Whether this machine should get the 3D reel.
 *
 * Lives in its own module because two components need the same answer and must
 * not disagree: the home page uses it to decide whether the hero draws its own
 * reel, and ReelScroller uses it to decide whether to build the scroll stage
 * that draws the shared one. If they ever disagreed the page would end up with
 * either two reels or none.
 *
 * ## Why there is no longer a width test
 *
 * There used to be one: below 1024px this returned false and every phone got
 * the flat fallback. Two things were wrong with that.
 *
 * The first is that it answers the wrong question. Screen width says nothing
 * about whether a device can run the scene — a 2019 phone has a better GPU than
 * a 2013 laptop, and both are wider than the threshold or narrower than it for
 * reasons that have nothing to do with the answer. What actually matters is
 * whether there is a WebGL context, whether the reader has asked us not to
 * spend their data, and whether the machine has the memory for it. Those are
 * all things the browser will tell you if you ask.
 *
 * The second is that width *changes*. Both callers read this once, during their
 * first render, because deciding late would swap a multi-screen section in
 * underneath the reader. A width-based answer therefore went stale the moment a
 * phone was rotated or a window was dragged across the breakpoint, and the two
 * callers — mounted at different times — could disagree about it. Everything
 * this now tests is fixed for the life of the page, so reading it once is safe.
 *
 * Width still matters to how the reel is *drawn*; see `reelGeometry`, where it
 * sets the frame width and the hero placement and is recomputed on resize.
 */
export function canRenderStage(): boolean {
  if (typeof window === "undefined") return false;

  const nav = navigator as Navigator & {
    connection?: { saveData?: boolean };
    deviceMemory?: number;
  };

  // The reader has asked their browser to spend as little of their data as it
  // can. Three.js is most of a megabyte; that request gets honoured.
  if (nav.connection?.saveData) return false;

  /*
   * Machines with under a gigabyte of memory.
   *
   * `deviceMemory` is Chromium-only and reports a rounded power of two, so this
   * is a floor rather than a measurement — it rules out the genuinely tiny
   * devices without pretending to grade the rest. Absent elsewhere, in which
   * case the WebGL test below is the only gate, which is the behaviour every
   * non-Chromium browser had anyway.
   */
  if (typeof nav.deviceMemory === "number" && nav.deviceMemory > 0 && nav.deviceMemory < 1) {
    return false;
  }

  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl2") ?? canvas.getContext("webgl");
    if (!gl) return false;

    /*
     * Hand the context straight back.
     *
     * A browser will only keep so many WebGL contexts alive at once and drops
     * the oldest when it runs out — and phones have the smallest allowance.
     * Probing for one and abandoning it means the page is holding a context it
     * will never draw with, against the one the reel actually needs.
     */
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    return true;
  } catch {
    return false;
  }
}
